import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { PanelScoutUser, PanelScoutPermission } from "../../types/panel-scout-user.types";

function getPanelscoutOrgNames(user: PanelScoutUser): string {
  const orgs = user.organizations;
  if (!orgs?.length) return user.organization ?? "—";
  const panelscoutOrgs = orgs.filter((o) => o.type === "panelscout");
  if (!panelscoutOrgs.length) return "—";
  return panelscoutOrgs.map((o) => o.name).join(", ");
}

function getPanelScoutPermissionNames(user: PanelScoutUser): string[] {
  const perms = user.permissions?.groups;
  if (!perms?.length) return [];
  const objects = perms.filter(
    (p): p is PanelScoutPermission =>
      typeof p === "object" && p != null && "name" in p && typeof (p as PanelScoutPermission).name === "string"
  );
  if (objects.length) {
    return objects
      .filter((p) => (p.name ?? "").toLowerCase().includes("panel_scout"))
      .map((p) => p.name);
  }
  return (perms as string[]).filter((s) => s.toLowerCase().includes("panel_scout"));
}

interface PanelScoutUsersTableProps {
  users: PanelScoutUser[];
}

const cellPadding = "py-4 px-4";

const PanelScoutUsersTable: React.FC<PanelScoutUsersTableProps> = ({
  users,
}) => {
  return (
    <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={cellPadding}>
              Name
            </TableHead>
            <TableHead className={cellPadding}>
              Email
            </TableHead>
            <TableHead className={cellPadding}>
              Organization
            </TableHead>
            <TableHead className={cellPadding}>
              Permissions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={4}
                className={`py-12 text-center text-gray-500 ${cellPadding}`}
              >
                No users found.
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <TableRow
                key={user.id}
                className="hover:bg-gray-50"
              >
                <TableCell className={`font-medium text-gray-900 ${cellPadding}`}>
                  <div>
                    <div>
                      {user.name}
                    </div>
                    <div className="text-sm font-normal text-gray-500 mt-0.5">
                      {user.fullname ?? "—"}
                    </div>
                  </div>
                </TableCell>
                <TableCell className={`text-gray-600 ${cellPadding}`}>
                  {user.email}
                </TableCell>
                <TableCell className={`text-gray-600 ${cellPadding}`}>
                  {getPanelscoutOrgNames(user)}
                </TableCell>
                <TableCell className={`text-gray-600 ${cellPadding}`}>
                  {(() => {
                    const names = getPanelScoutPermissionNames(user);
                    return names.length ? (
                      <span className="text-sm">
                        {names.join(", ")}
                      </span>
                    ) : (
                      <span className="text-gray-400">
                        —
                      </span>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default PanelScoutUsersTable;
